import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Eye, EyeSlash, X } from "@phosphor-icons/react";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/leads").then((r) => setLeads(r.data || []));
  useEffect(() => { load(); }, []);

  const toggleRead = async (lead) => {
    try {
      await api.patch(`/leads/${lead.id}?read=${!lead.read}`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Supprimer la demande de ${lead.prenom} ${lead.nom} ?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Demande supprimée");
      if (selected?.id === lead.id) setSelected(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-leads" className="p-8 md:p-10">
      <div className="label-caps mb-3">Demandes reçues</div>
      <h1 className="font-serif text-3xl md:text-4xl text-ducs-cream">Pipeline de leads</h1>
      <p className="text-ducs-mist mt-2">{leads.length} demandes au total · {leads.filter((l) => !l.read).length} non lues.</p>

      <div className="mt-10 card-ducs overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-ducs-mist text-xs uppercase tracking-wider bg-ducs-night/40">
            <tr>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Identité</th>
              <th className="px-5 py-3">Contact</th>
              <th className="px-5 py-3">Statut / besoin</th>
              <th className="px-5 py-3">État</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-ducs-gold/10 hover:bg-ducs-navy/40 cursor-pointer" onClick={() => setSelected(l)}>
                <td className="px-5 py-3 text-ducs-mist">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                <td className="px-5 py-3 text-ducs-cream">{l.prenom} {l.nom}</td>
                <td className="px-5 py-3 text-ducs-cream/80">
                  <div>{l.email}</div>
                  <div className="text-xs text-ducs-mist">{l.telephone}</div>
                </td>
                <td className="px-5 py-3">
                  <div className="text-ducs-cream/80">{l.besoin}</div>
                  <div className="text-xs text-ducs-mist">{l.statut}</div>
                </td>
                <td className="px-5 py-3">
                  {l.read ? <span className="text-ducs-mist">Lue</span> : <span className="text-ducs-gold">Nouvelle</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleRead(l); }}
                    title={l.read ? "Marquer non lue" : "Marquer lue"}
                    className="text-ducs-cream/70 hover:text-ducs-gold mr-3"
                    data-testid={`toggle-read-${l.id}`}
                  >
                    {l.read ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(l); }}
                    className="text-ducs-cream/70 hover:text-red-400"
                    data-testid={`delete-lead-${l.id}`}
                  >
                    <Trash size={18} />
                  </button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-ducs-mist">Aucune demande pour le moment.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-ducs-navy border border-ducs-gold/30 max-w-lg w-full p-8 rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="label-caps">Demande</div>
                <h3 className="font-serif text-2xl text-ducs-cream mt-1">{selected.prenom} {selected.nom}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-ducs-cream/70 hover:text-ducs-gold"><X size={22} /></button>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <Row label="Email" value={<a className="text-ducs-gold" href={`mailto:${selected.email}`}>{selected.email}</a>} />
              <Row label="Téléphone" value={<a className="text-ducs-gold" href={`tel:${selected.telephone}`}>{selected.telephone}</a>} />
              <Row label="Statut" value={selected.statut} />
              <Row label="Besoin" value={selected.besoin} />
              <Row label="Date" value={new Date(selected.created_at).toLocaleString("fr-FR")} />
            </div>
            {selected.message && (
              <div className="mt-6">
                <div className="label-caps mb-2">Message</div>
                <p className="text-ducs-cream/85 leading-relaxed whitespace-pre-line">{selected.message}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-ducs-gold/10 pb-2">
      <div className="text-ducs-mist text-xs uppercase tracking-wider">{label}</div>
      <div className="col-span-2 text-ducs-cream">{value}</div>
    </div>
  );
}
